import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import he from './locales/he.json';

export const SUPPORTED_LANGUAGES = ['he', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_DIRECTION: Record<Language, 'rtl' | 'ltr'> = {
  he: 'rtl',
  en: 'ltr',
};

/**
 * Hebrew is the default and the fallback (REQUIREMENTS §2.8) — an untranslated
 * key must surface as Hebrew, not English, because Hebrew is what the unit
 * actually reads.
 *
 * There is no browser-side detection and no per-user toggle (owner,
 * 2026-09-19, story 38): the language is one company-wide setting the admin
 * picks on `/settings`; `useAppLanguage` reads it from `/api/public/config`
 * on boot and switches to it. Until that answer arrives the app is in Hebrew.
 */
await i18n.use(initReactI18next).init({
  resources: {
    he: { translation: he },
    en: { translation: en },
  },
  lng: 'he',
  fallbackLng: 'he',
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: { escapeValue: false },
});

export function applyDirection(language: string): void {
  const lang = (SUPPORTED_LANGUAGES as readonly string[]).includes(language)
    ? (language as Language)
    : 'he';
  document.documentElement.lang = lang;
  document.documentElement.dir = LANGUAGE_DIRECTION[lang];
}

applyDirection(i18n.resolvedLanguage ?? 'he');
i18n.on('languageChanged', applyDirection);

export default i18n;
